<script lang="ts">
  /**
   * Who can open what — one screen, every portal except Codebots.
   *
   * Access used to live in three unrelated places with no view over all of it: Fabric's
   * custom claims for the six programme portals, HA Portal's `settings/access`, and
   * Finance's `settings/household`. "Does Mandri have Finance?" had three possible
   * answers and no way to see them together.
   *
   * The grid is deliberately the whole screen. A person is a row, a portal is a column,
   * and the cell says what would happen if they signed in **right now** — not what was
   * intended. Where the two systems disagree the cell says so, because "Fabric grants
   * it but the app's list does not" is the single most useful thing here and is
   * invisible in any per-system view.
   *
   * Every mutation is elsewhere: roles and modules on People & portals, credentials
   * below. This screen answers the question; it does not quietly become another place
   * access is granted, which is exactly the sprawl it exists to end.
   */
  import { Icon, Panel } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";

  type PortalAccess = { portalId: string; name: string; allowed: boolean; via: string; mismatch: string | null };
  type Person = {
    email: string;
    displayName: string;
    uid: string | null;
    role: string | null;
    modules: string[];
    expiresAt: number | null;
    expired: boolean;
    legacy: Array<{ project: string; uid: string; providers: string[]; disabled: boolean }>;
    fabricOnly: boolean;
    legacyOnly: boolean;
    portals: PortalAccess[];
  };
  type Payload = {
    people: Person[];
    unreadable: string[];
    portals: Array<{ id: string; name: string; minRole: string; via: string }>;
  };
  type LegacyPayload = {
    users: Array<{
      project: string;
      projectLabel: string;
      uid: string;
      email: string | null;
      disabled: boolean;
      providers: string[];
      lastSignIn: string | null;
    }>;
    unreadable: string[];
    projects: Array<{ id: string; label: string; note: string }>;
  };

  let data = $state<Payload | null>(null);
  let legacy = $state<LegacyPayload | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let note = $state<{ kind: "ok" | "warn"; text: string } | null>(null);
  let busy = $state<string | null>(null);

  /** A reset link, shown once. Never stored, never logged. */
  let link = $state<{ email: string; url: string } | null>(null);

  let newEmail = $state("");
  let newProject = $state("helloliam-ha-dashboard");

  const mismatches = $derived(
    (data?.people ?? []).flatMap((p) => p.portals.filter((x) => x.mismatch).map((x) => ({ person: p, cell: x }))),
  );

  async function load() {
    loading = true;
    try {
      const [d, l] = await Promise.all([
        api.get<Payload>("/admin/people"),
        api.get<LegacyPayload>("/admin/legacy/users").catch(() => null),
      ]);
      data = d;
      legacy = l;
      error = null;
    } catch (e) {
      error = e instanceof ApiError ? e.message : "Could not read access";
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    void load();
  });

  /**
   * Every action here is step-up gated on the server. A 401 with `step_up_required`
   * means "prove it is still you", which is a prompt rather than a failure.
   */
  async function act<T>(id: string, fn: () => Promise<T>, ok: string): Promise<T | null> {
    busy = id;
    note = null;
    try {
      const r = await fn();
      note = { kind: "ok", text: ok };
      await load();
      return r;
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "That did not work";
      note = {
        kind: "warn",
        text: /step.?up|recent/i.test(msg) ? "Sign in again to confirm — this action needs a fresh sign-in." : msg,
      };
      return null;
    } finally {
      busy = null;
    }
  }

  async function invite() {
    if (!newEmail.trim()) return;
    const r = await act(
      "invite",
      () =>
        api.post<{ resetLink: string; existed: boolean }>("/admin/legacy/users", {
          project: newProject,
          email: newEmail.trim(),
        }),
      "Account ready. Send them the link below.",
    );
    if (r) {
      link = { email: newEmail.trim(), url: r.resetLink };
      newEmail = "";
    }
  }

  async function resend(project: string, email: string) {
    const r = await act(
      `reset-${email}`,
      () => api.post<{ resetLink: string }>("/admin/legacy/users/reset", { project, email }),
      "New link ready.",
    );
    if (r) link = { email, url: r.resetLink };
  }

  const toggle = (project: string, uid: string, email: string | null, disabled: boolean) =>
    act(
      `dis-${uid}`,
      () => api.post("/admin/legacy/users/disabled", { project, uid, disabled }),
      `${disabled ? "Disabled" : "Re-enabled"} ${email ?? uid}.`,
    );

  const pretty = (s: string) => s.replace(/_/g, " ");
</script>

<div class="wrap">
  <header class="head">
    <h1>Who can open what</h1>
    <p class="sub">
      Every portal except Codebots. Each cell is what would happen if that person signed in now —
      the six newer portals read Fabric's claims directly; HA Portal, Screening Room and Finance read
      their own email lists, which Fabric writes each morning.
    </p>
  </header>

  {#if note}
    <div class="attn" class:attn--warn={note.kind === "warn"}>
      <span class="attn__badge" aria-hidden="true"><Icon name={note.kind === "warn" ? "alert" : "check"} size={12} /></span>
      <span class="attn__text">{note.text}</span>
      <button class="lnk" onclick={() => (note = null)}>Dismiss</button>
    </div>
  {/if}

  {#if link}
    <!-- Shown once, on the owner's screen, and never persisted. Fabric does not choose
         or store a password; this link lets the person set their own with Google. -->
    <div class="attn">
      <span class="attn__badge" aria-hidden="true"><Icon name="shield" size={12} /></span>
      <span class="attn__text">
        <strong>Password link for {link.email}</strong> — send this to them. It expires, and it is not
        stored anywhere.
        <input class="linkbox num" readonly value={link.url} onclick={(e) => e.currentTarget.select()} />
      </span>
      <button class="lnk" onclick={() => (link = null)}>Done</button>
    </div>
  {/if}

  <!-- ── disagreements first, because they are the actionable part ────────── -->
  {#if mismatches.length > 0}
    <section class="card">
      <div class="sec">
        <span class="kicker">Worth a look</span>
        <span class="sub">{mismatches.length}</span>
      </div>
      {#each mismatches as m (m.person.email + m.cell.portalId)}
        <div class="sub check">
          <Icon name="alert" size={12} />
          <strong>{m.person.displayName}</strong> · {m.cell.name} — {m.cell.mismatch}
        </div>
      {/each}
    </section>
  {/if}

  <!-- ── the grid ─────────────────────────────────────────────────────────── -->
  <section class="card">
    <div class="sec"><span class="kicker">Access</span><span class="sub">{data?.people.length ?? 0} people</span></div>
    <Panel {loading} {error} isEmpty={(data?.people.length ?? 0) === 0} onretry={load} rows={4}>
      {#snippet empty()}
        Nobody has signed in yet. Claims can only attach to an account that exists, so have each
        person sign in once, then set their role on People &amp; portals.
      {/snippet}

      <div class="scroll">
        <table>
          <thead>
            <tr>
              <th>Person</th>
              <th>Role</th>
              {#each data?.portals ?? [] as p (p.id)}
                <th class="c" title="{p.name} · needs {p.minRole} · via {p.via}">{p.name}</th>
              {/each}
            </tr>
          </thead>
          <tbody>
            {#each data?.people ?? [] as person (person.email)}
              <tr>
                <td>
                  <span class="nm">{person.displayName}</span>
                  <span class="em num">{person.email}</span>
                  {#if person.legacyOnly}
                    <span class="tag tag--warn">not in Fabric</span>
                  {/if}
                  {#if person.expired}<span class="tag tag--warn">window closed</span>{/if}
                </td>
                <td>
                  {#if person.role}
                    <span class="pill">{person.role}</span>
                  {:else}
                    <span class="pill quiet">none</span>
                  {/if}
                </td>
                {#each person.portals as cell (cell.portalId)}
                  <td class="c">
                    {#if cell.mismatch}
                      <!-- Glyph + colour + a tooltip sentence. Never colour alone. -->
                      <span class="mark mark--warn" title={cell.mismatch}><Icon name="alert" size={13} /></span>
                    {:else if cell.allowed}
                      <span class="mark mark--yes" title="Can open {cell.name}"><Icon name="check" size={13} /></span>
                    {:else}
                      <span class="mark mark--no" title="Cannot open {cell.name}">·</span>
                    {/if}
                  </td>
                {/each}
              </tr>
            {/each}
          </tbody>
        </table>
      </div>

      {#if (data?.unreadable.length ?? 0) > 0}
        <p class="sub pad">
          <Icon name="alert" size={12} />
          Could not read: {data?.unreadable.join(", ")} — those columns are unknown, not "no".
        </p>
      {/if}
    </Panel>
  </section>

  <!-- ── credentials in the older projects ───────────────────────────────── -->
  <section class="card">
    <div class="sec">
      <span class="kicker">Sign-in accounts in the older portals</span>
      <span class="sub">HA Portal, Screening Room and Finance keep their own</span>
    </div>
    <p class="sub pad">
      Those three verify tokens against their own projects, so an account has to exist there for
      somebody to sign in. Fabric creates it and issues a link they use to set their own password —
      no password is ever chosen, stored, or shown here.
    </p>

    <div class="grid">
      <label>
        <span class="lb">Add somebody to</span>
        <select bind:value={newProject}>
          {#each legacy?.projects ?? [] as p (p.id)}<option value={p.id}>{p.label}</option>{/each}
        </select>
      </label>
      <label>
        <span class="lb">Their email</span>
        <input bind:value={newEmail} placeholder="name@example.com" autocomplete="off" />
      </label>
      <div class="acts">
        <button class="btn-primary" disabled={busy === "invite" || !newEmail.trim()} onclick={invite}>
          {busy === "invite" ? "Creating…" : "Create and get a link"}
        </button>
      </div>
    </div>

    {#if legacy}
      <div class="scroll">
        <table>
          <thead>
            <tr><th>Account</th><th>Portal</th><th>Signs in with</th><th>Last seen</th><th></th></tr>
          </thead>
          <tbody>
            {#each legacy.users as u (u.project + u.uid)}
              <tr class:row--off={u.disabled}>
                <td>
                  <span class="nm">{u.email ?? u.uid}</span>
                  {#if u.disabled}<span class="tag tag--warn">disabled</span>{/if}
                </td>
                <td class="sub">{u.projectLabel}</td>
                <td class="sub">
                  {u.providers.map((p) => (p === "google.com" ? "Google" : p === "password" ? "password" : p)).join(" + ") || "—"}
                </td>
                <td class="sub num">{u.lastSignIn ? new Date(u.lastSignIn).toISOString().slice(0, 10) : "never"}</td>
                <td class="r">
                  {#if u.email}
                    <button class="lnk" disabled={busy === `reset-${u.email}`} onclick={() => resend(u.project, u.email!)}>
                      Password link
                    </button>
                  {/if}
                  <button class="lnk" disabled={busy === `dis-${u.uid}`} onclick={() => toggle(u.project, u.uid, u.email, !u.disabled)}>
                    {u.disabled ? "Re-enable" : "Disable"}
                  </button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
      {#if legacy.unreadable.length > 0}
        <p class="sub pad"><Icon name="alert" size={12} /> Could not read: {legacy.unreadable.join(", ")}.</p>
      {/if}
    {/if}
  </section>
</div>

<style>
  .wrap { display: flex; flex-direction: column; gap: var(--sp-4); padding-bottom: var(--sp-6); }
  .head { padding: var(--sp-5) 0 0; }
  h1 { margin: 0 0 6px; font-size: var(--fs-h2); font-weight: 800; letter-spacing: -0.7px; color: var(--tx); }
  .head .sub { max-width: 72ch; line-height: 1.5; }

  .scroll { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; font-size: var(--fs-small); }
  th {
    padding: 8px 10px; text-align: left; font-size: var(--fs-micro); font-weight: 800;
    letter-spacing: 0.4px; text-transform: uppercase; color: var(--mut);
    border-bottom: 1px solid var(--line); white-space: nowrap;
  }
  td { padding: 9px 10px; border-bottom: 1px solid var(--line); vertical-align: top; }
  tr:last-child td { border-bottom: none; }
  .c { text-align: center; }
  .r { text-align: right; white-space: nowrap; }
  .row--off { opacity: 0.55; }

  .nm { display: block; font-weight: 700; color: var(--tx); }
  .em { display: block; margin-top: 1px; font-size: var(--fs-micro); color: var(--mut); }

  .pill {
    display: inline-block; padding: 2px 8px; border-radius: var(--r-pill);
    font-size: var(--fs-micro); font-weight: 800; box-shadow: inset 0 0 0 1px var(--line);
  }
  .pill.quiet { color: var(--mut); }

  .tag {
    display: inline-block; margin-top: 3px; padding: 1px 6px; border-radius: var(--r-pill);
    font-size: var(--fs-micro); font-weight: 800;
  }
  /* Amber, with the word beside it. Never colour alone. */
  .tag--warn { color: var(--warn); box-shadow: inset 0 0 0 1px var(--warn); }

  .mark { display: inline-grid; place-items: center; width: 22px; height: 22px; border-radius: 50%; }
  /* Blue for yes — there is no green anywhere in this estate. */
  .mark--yes { color: var(--ok); }
  .mark--warn { color: var(--warn); }
  .mark--no { color: var(--mut); font-weight: 800; }

  .linkbox {
    display: block; width: 100%; margin-top: 6px; padding: 6px 8px;
    font-size: var(--fs-micro); background: var(--fill); color: var(--tx2);
    border: 1px solid var(--line); border-radius: var(--r-control);
  }
</style>
